import React from 'react';
import { Breakpoints } from '@volkswagen-onehub/components-core';
import { Spinner } from '@vw-marketing/us-components';
import {
  NavigationLayout,
  NavigationContainer,
  NavigationScrollContainer,
  NavigationFooter,
} from '../styles';
import { NavMenu } from '../../NavMenu/NavMenu';
import { useStore } from '../../../store';
import { ModelCard } from '../../ModelCard/ModelCard';
import { Box } from '../../Box';
import { useViewport, ViewportType } from '../../../hooks/use-viewport';
import { useBodyScrollLock } from '../../../hooks/custom/use-body-scroll-lock';
import { CTAButton } from '../../Button/CTAButton';
import { AutoCompleteInput } from '../../Input/AutoCompleteInput';
import { Option } from '../../Input/types';
import { downloadUrl } from '../../../utils/download';
import { useQueryParamValue } from '../../../hooks/custom/use-query-param-value';
import { FullQSGTemplate } from '../Content/FullQSGTemplate';
import { onVWBasicDownloadClickEvent } from '../../../tagging/tagging-helpers';
import { useTrackingManager } from '../../../hooks/use-tracking-manager';
import { setNotificationMessage } from '../../../store/slices/qsg/actions';
import { useSearchAutocomplete } from '../../../hooks/custom/use-search-autocomplete';
import {
  SectionModel,
  NavigationSection,
} from '../../../fetch-data/graphql/types';

interface NavigationProps {
  onChangeModel: () => void;
  headerToggle: boolean;
  isSidebarOpen: boolean;
  goToSection: (section: SectionModel | NavigationSection) => void;
  onSearch: (option: Option | null, isAutocompleteClick?: boolean) => void;
}

export const Navigation = ({
  onChangeModel,
  headerToggle,
  isSidebarOpen,
  goToSection,
  onSearch,
}: NavigationProps) => {
  const [state, dispatch] = useStore();
  const { qsgNavigation, qsgSearchTerm, qsgContent } = state.qsg;
  const trackingManager = useTrackingManager();
  const urlSearchTerm = useQueryParamValue('search', '');

  const viewport = useViewport(Breakpoints.b560, Breakpoints.b1280);
  const isMobile = viewport !== ViewportType.DESKTOP; // Only prevent scroll on mobile/tablet

  const navSections = qsgNavigation?.sections ?? [];

  const modelId = qsgNavigation?.modelId ?? '-';
  const model = qsgNavigation?.modelName ?? '-';
  const modelYear = qsgNavigation?.modelYear ?? '-';
  const jellybean = qsgNavigation?.jellybean?._publishUrl ?? '';
  const pdfFile = qsgNavigation?.pdfFile?._publishUrl ?? '';

  const isNavigationLoading = Boolean(!qsgNavigation);
  // Check if navigation is initialized (equivalent to hasInit)
  const hasInit = Boolean(qsgNavigation);

  const { autocompleteOptions, handleSearchChange, qsgSearchOption } =
    useSearchAutocomplete({
      qsgContent,
      qsgSearchTerm,
      urlSearchTerm,
      limit: 10,
    });

  // Prevent body scrolling when navigation is open on mobile/tablet only
  useBodyScrollLock(isSidebarOpen, isMobile);

  const handlePdfDownload = () => {
    onVWBasicDownloadClickEvent(trackingManager, {
      modelId,
      modelName: model,
      modelYear,
    });

    if (pdfFile) {
      downloadUrl(pdfFile);
    } else {
      dispatch(
        setNotificationMessage(
          'Unable to generate PDF. Please try again later.'
        )
      );
    }
  };

  return (
    <>
      {/* Hidden FullQSGTemplate for PDF generation */}
      {hasInit && (
        <FullQSGTemplate
          isPrintMode={false}
          hasInit={hasInit}
          isHidden={true}
        />
      )}
      <NavigationLayout $isOpen={isSidebarOpen} $headerToggle={headerToggle} $isMobile={isMobile}>
        <NavigationScrollContainer>
          <ModelCard
            model={model}
            modelYear={modelYear}
            modelImage={jellybean}
            onChangeModel={onChangeModel}
          />

          <NavigationContainer>
            <AutoCompleteInput
              inputId='searchField'
              label='Search'
              hideLabel={true}
              required={true}
              defaultValue={urlSearchTerm || undefined}
              value={qsgSearchOption}
              options={autocompleteOptions}
              onDebounceChange={handleSearchChange}
              onSelect={(opt, isAutocompleteClick) =>
                onSearch(opt, isAutocompleteClick)
              }
              placeholder='What do you need help with?'
              syncValue={false}
            />
            {isNavigationLoading ? (
              <Box
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flex: 1,
                  minHeight: 0,
                  paddingBottom: '80px',
                }}
              >
                <Spinner />
              </Box>
            ) : (
              <NavMenu
                sections={navSections ?? []}
                onCloseSidebar={goToSection}
              />
            )}
          </NavigationContainer>
        </NavigationScrollContainer>
        {pdfFile && (
          <NavigationFooter>
            <NavigationContainer>
              <CTAButton
                tag='button'
                emphasis='secondary'
                onClick={handlePdfDownload}
                style={{
                  width: '100%',
                  marginTop: '1rem',
                  '> button': {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  },
                  '> button > span': {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                  },
                }}
              >
                Download PDF
              </CTAButton>
            </NavigationContainer>
          </NavigationFooter>
        )}
      </NavigationLayout>
    </>
  );
};
